import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd from '@/components/feature/JsonLd';

export default function Article() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">
            {t('notFound.title')}
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            {t('notFound.text')}
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
          >
            {t('notFound.backToHome')}
            <i className="ri-arrow-left-line text-sm" />
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const siteUrl = (import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');
  const articleUrl = siteUrl ? `${siteUrl}/articles/${article.slug}/` : undefined;

  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    url: articleUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    author: {
      '@type': 'Organization',
      name: 'Basetao Sheet Guide',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Sheet Guide',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
      },
    },
    datePublished: '2026-08-01',
    dateModified: '2026-08-18',
    inLanguage: 'en',
  };

  return (
    <>
      <JsonLd data={blogPostingSchema} />
      <Navbar />

      <main className="mx-auto max-w-content px-4 md:px-6 py-8 md:py-12">
        <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            {t('nav.home')}
          </Link>
          <span className="mx-2 text-foreground-300">/</span>
          <span className="text-foreground-700">{article.title}</span>
        </nav>

        <article className="mx-auto mt-6 max-w-3xl">
          <header>
            <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-900">
              {article.tag}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.intro}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line text-sm" />
                {t('article.author')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm" />
                Updated {article.updated}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-sm" />
                {article.readTime}
              </span>
            </div>
          </header>

          <div className="mt-8 space-y-8 text-base leading-relaxed text-foreground-700">
            {article.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para) => (
                  <p key={para} className="mt-3">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {t('article.faqTitle')}
              </h2>
              <div className="mt-4 space-y-3">
                {article.faqs.map((faq) => (
                  <div
                    key={faq.q}
                    className="rounded-lg border border-background-200/70 bg-background-50 p-4"
                  >
                    <h3 className="text-sm font-semibold text-foreground-900">{faq.q}</h3>
                    <p className="mt-2 text-sm text-foreground-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {t('article.relatedTitle')}
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/articles/${item.slug}/`}
                    className="rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <span className="inline-flex rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                      {item.tag}
                    </span>
                    <p className="mt-2 text-sm font-semibold leading-snug text-foreground-900">
                      {item.title}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <div className="mt-10 rounded-lg border border-background-200/70 bg-background-100/60 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-950">
              {t('article.ctaTitle')}
            </h3>
            <p className="mt-2 text-sm text-foreground-600">
              {t('article.ctaText')}
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                {t('article.backToHome')}
                <i className="ri-arrow-left-line text-sm" />
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-accent-600 transition-colors"
              >
                {t('article.visitMainSite')}
                <i className="ri-external-link-line text-sm" />
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}